import { Card, CardContent, CardHeader, Typography, Chip } from '@mui/material';
import Avatar from '../../../avatar/Avatar';
import ReadOnlyTextEditor from '../../../TextEditor/ReadOnlyTextEditor';
import { format, formatDistanceToNowStrict } from 'date-fns';
import MenuIconButton from '../../../button/MenuIconButton';
import { useSelector } from 'react-redux';
import ChipMediaFeed from '../../../chip/ChipMediaFeed';
import './Card.css';

const CardFeed = ({ feed, isPreview, onModal }) => {
  const permissions = useSelector((state) => state?.session?.permissions?.feed || {});

  // TODO: Harcoded post type styles, move to backend later
  const postTypeConfig = {
    announcement: {
      label: 'Announcement',
      color: '#1d4ed8', // text-blue-700
      backgroundColor: '#eff6ff', // bg-blue-50
      borderColor: '#eff6ff' // border-blue-400
    },
    document: {
      label: 'Document',
      color: '#047857', // text-emerald-700
      backgroundColor: '#ecfdf5', // bg-emerald-50
      borderColor: '#ecfdf5' // border-emerald-400
    },
    achievement: {
      label: 'Achievement',
      color: '#b45309', // text-amber-700
      backgroundColor: '#fffbeb', // bg-amber-50
      borderColor: '#fffbeb' // border-amber-400
    },
    task: {
      label: 'Task',
      color: '#be123c', // text-rose-700
      backgroundColor: '#fff1f2', // bg-rose-50
      borderColor: '#fff1f2' // border-rose-400
    }
  };

  const formatRelative = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    const diffMs = Date.now() - date.getTime();

    // fecha futura -> mostrar fecha completa
    if (diffMs < 0) return format(date, 'PPpp');

    const days = diffMs / (1000 * 60 * 60 * 24);
    const THRESHOLD_DAYS = 5;

    // Hasta 5 días -> "x hours/minutes/seconds ago" / "x days ago"
    if (days < THRESHOLD_DAYS) {
      return formatDistanceToNowStrict(date, { addSuffix: true });
    }

    // Más de 5 días -> mostrar fecha legible
    return format(date, 'PPpp');
  };

  const getPostTypeChip = () => {
    const postType = feed?.postType?.value;
    if (!postType || !postTypeConfig[postType]) return null;

    const config = postTypeConfig[postType];

    // Icon mapping for each post type
    const iconMap = {
      announcement: '📢', // Megaphone for announcements
      document: '📄', // Document page
      achievement: '🏆', // Trophy for achievements
      task: '📝' // Memo/clipboard for tasks
    };

    return (
      <Chip
        label={
          <div className='flex items-center gap-1'>
            <span className='pr-1'>{iconMap[postType]}</span>
            <span>{config.label}</span>
          </div>
        }
        size='small'
        sx={{
          backgroundColor: config.backgroundColor,
          color: config.color,
          border: `1px solid ${config.borderColor}`,
          height: '24px',
          '& .MuiChip-label': {
            padding: '0 8px',
            fontWeight: 600,
            fontSize: '0.875rem'
          }
        }}
      />
    );
  };

  return (
    <Card
      className={`feed-card feed-card--${feed?.postType?.value || 'other'}`}
      sx={{
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        borderRadius: '12px',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        transition: 'all 0.2s ease-in-out',
        marginBottom: '1rem',
        '&:hover': {
          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)',
          transform: 'translateY(-2px)'
        }
      }}
    >
      <CardHeader
        avatar={
          <Avatar
            size='dashboard'
            variant='circular'
            alt={feed?.updatedBy?.code}
            src={
              feed?.updatedBy?.photo
                ? `photos/${feed?.updatedBy?.photo}`
                : `photos/${feed?.updatedBy?.userId}.jpg`
            }
            sx={{
              width: 44,
              height: 44,
              border: '2px solid rgba(255, 255, 255, 0.8)',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)'
            }}
          />
        }
        action={
          <div className='flex items-center gap-2'>
            {getPostTypeChip()}
            {!isPreview && (
              <MenuIconButton
                id='feed-card-options-button'
                icon={['far', 'ellipsis']}
                aria-label='settings'
                title='Feed Options'
                items={[
                  {
                    label: 'Edit Feed',
                    icon: 'pen',
                    permissions: permissions?.updated && onModal,
                    onClick: () => onModal('edit', feed)
                  },
                  {
                    label: 'Delete Feed',
                    icon: 'trash-alt',
                    permissions: permissions?.deleted && onModal,
                    onClick: () => onModal('delete', feed)
                  }
                ]}
                sx={{
                  opacity: 0.6,
                  transition: 'opacity 0.2s',
                  '&:hover': {
                    opacity: 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              />
            )}
          </div>
        }
        title={
          <div className='space-y-1'>
            <Typography
              variant='subtitle1'
              sx={{
                fontWeight: 600,
                fontSize: '1rem',
                color: '#1a202c'
              }}
            >
              {`${feed?.updatedBy?.code} - ${feed?.updatedBy?.fullName}`}
            </Typography>
            {feed.updatedBy?.roles?.length > 0 && (
              <div className='flex flex-wrap gap-1'>
                {feed.updatedBy.roles.map((role) => (
                  <Chip
                    key={`feed-${feed._id}-role-${role.id}`}
                    label={role.name}
                    size='small'
                    variant='outlined'
                    sx={{
                      height: '22px',
                      fontSize: '0.7rem',
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      borderColor: 'rgba(0, 0, 0, 0.12)',
                      color: '#6b7280'
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        }
        subheader={
          <div className='flex items-center gap-1 mt-2'>
            <span style={{ fontSize: '14px' }}>🕐</span>
            <Typography
              variant='caption'
              sx={{
                color: '#9ca3af',
                fontSize: '0.8rem',
                fontWeight: 400
              }}
            >
              {formatRelative(feed.pusblishedAt)}
            </Typography>
          </div>
        }
        sx={{
          paddingBottom: '12px',
          '& .MuiCardHeader-content': {
            overflow: 'hidden'
          }
        }}
      />

      <CardContent sx={{ paddingTop: '8px' }}>
        {/* Enhanced Title */}
        <Typography
          variant='h6'
          component='div'
          sx={{
            marginBottom: '16px',
            fontWeight: 600,
            fontSize: '18px',
            lineHeight: 1.4,
            color: '#1a202c'
          }}
        >
          {feed?.title || ''}
        </Typography>

        {/* Content with better styling */}
        <div
          className='prose prose-sm max-w-none'
          style={{
            marginBottom: '20px',
            fontSize: '14px',
            lineHeight: 1.6,
            color: '#4a5568'
          }}
        >
          <ReadOnlyTextEditor content={feed.content} />
        </div>

        {/* Enhanced Attachments Section */}
        {feed?.attachment && feed?.attachment?.length > 0 && (
          <div className='border-t pt-4 mt-4' style={{ borderColor: '#e5e7eb' }}>
            <div className='flex items-center gap-2 mb-3'>
              <span style={{ fontSize: '16px' }}>📎</span>
              <Typography
                variant='subtitle2'
                sx={{
                  color: '#6b7280',
                  fontWeight: 500,
                  fontSize: '12px'
                }}
              >
                Attachments ({feed.attachment.length})
              </Typography>
            </div>
            <div className='flex flex-wrap gap-2'>
              {feed.attachment.map((data, index) => (
                <ChipMediaFeed
                  key={`feed-${feed._id}-attachment-${index}-${data._id}-${data.type}`}
                  index={index}
                  data={data}
                />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CardFeed;
